import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import Button from 'shared/components/UC/Button';
import adminService from '../../services/adminService';
import styles from './EvaluatorNotesModal.module.css';

interface EvaluatorNote {
  id: string;
  text: string;
  createdAt: string;
  createdByName: string;
}

interface EvaluatorNotesModalProps {
  isOpen: boolean;
  evaluatorId: string;
  evaluatorName: string;
  isExternal: boolean;
  onClose: () => void;
  onProfileChanged?: () => void;
}

const EvaluatorNotesModal: React.FC<EvaluatorNotesModalProps> = ({
  isOpen,
  evaluatorId,
  evaluatorName,
  isExternal,
  onClose,
  onProfileChanged,
}) => {
  const [score, setScore] = useState<string>('');
  const [notes, setNotes] = useState<EvaluatorNote[]>([]);
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [addingNote, setAddingNote] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    adminService.getEvaluatorProfile(evaluatorId).then((profile: any) => {
      setScore(profile.score !== null ? String(profile.score) : '');
      setNotes(profile.notes ?? []);
    });
  }, [isOpen, evaluatorId]);

  const handleSaveScore = async () => {
    setSaving(true);
    try {
      const parsed = score === '' ? null : parseFloat(score);
      await adminService.updateEvaluatorScore(evaluatorId, parsed);
      onProfileChanged?.();
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    try {
      await adminService.addEvaluatorNote(evaluatorId, newNote.trim());
      const profile: any = await adminService.getEvaluatorProfile(evaluatorId);
      setNotes(profile.notes ?? []);
      setNewNote('');
      onProfileChanged?.();
    } finally {
      setAddingNote(false);
    }
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="modal-overlay"
      onClick={onClose}
    >
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h2 className="uc-h4">Ficha interna — {evaluatorName}</h2>
            {isExternal && <span className={styles.externalBadge}>Externo</span>}
          </div>
          <div className={styles.headerRight}>
            <span className={styles.dinvBadge}>Solo visible para DINV</span>
            <Button variant="secondary" size="sm" onClick={onClose}>
              ×
            </Button>
          </div>
        </div>

        <div className={styles.body}>
          <div className={styles.section}>
            <p className={`uc-label ${styles.sectionTitle}`}>Score de evaluador</p>
            <div className={styles.scoreRow}>
              <input
                type="number"
                min="1"
                max="5"
                step="0.1"
                value={score}
                onChange={(e) => setScore(e.target.value)}
                placeholder="1.0 – 5.0"
                className={styles.scoreInput}
              />
              <Button variant="primary" size="sm" onClick={handleSaveScore} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>
          </div>

          <div className={styles.section}>
            <p className={`uc-label ${styles.sectionTitle}`}>Historial de notas</p>
            {notes.length === 0 ? (
              <p className={styles.emptyNotes}>Sin notas registradas.</p>
            ) : (
              <div className={styles.noteList}>
                {notes.map((note) => (
                  <div key={note.id} className={styles.noteItem}>
                    <div className={styles.noteDate}>
                      {formatDate(note.createdAt)} — {note.createdByName}
                    </div>
                    <p className={styles.noteText}>{note.text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={styles.section}>
            <p className={`uc-label ${styles.sectionTitle}`}>Agregar nota</p>
            <div className={styles.addNoteArea}>
              <textarea
                className={styles.noteTextarea}
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Escriba una nota interna..."
                rows={3}
              />
              <Button
                variant="primary"
                size="sm"
                onClick={handleAddNote}
                disabled={addingNote || !newNote.trim()}
              >
                {addingNote ? 'Agregando...' : 'Agregar nota'}
              </Button>
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          <Button variant="secondary" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EvaluatorNotesModal;
